import { z } from "zod";
import { FlowFindingSchema, type FlowFinding } from "./flow-lint.js";

/**
 * Connect flow configuration - "landers" (requirements.md section 10).
 *
 * One contact flow serves many numbers by asking the portal what to do.
 * The organizing idea is that a phone number exists in exactly one
 * Connect instance, so keying configuration on a number or an ARN forces
 * a customer with three environments to author everything three times.
 * Instead:
 *
 *   profile  - authored once, environment-agnostic, refers to Connect
 *              resources by logical name and never by ARN
 *   inventory - what one environment actually has: logical name -> ARN
 *   binding   - which phone numbers reach a profile, per environment
 *
 * Resolution at call time is: dialed number -> profile (this
 * environment's bindings) -> logical references resolved to real ARNs
 * (this environment's inventory).
 */

/**
 * Everything is addressed as `(type, name)` - content and Connect
 * resources alike - so authoring, storage, the wire format and
 * resolution share one addressing model rather than three.
 *
 * Content types hold a literal value authored in the profile. Reference
 * types hold a logical name resolved to an ARN through the environment's
 * inventory. The caller never needs to know which is which.
 */
export const ContentFieldTypeSchema = z.enum(["announcement", "kv", "voice", "hours", "menu"]);
export type ContentFieldType = z.infer<typeof ContentFieldTypeSchema>;

export const ReferenceFieldTypeSchema = z.enum(["queue", "flow", "prompt", "hours_of_operation", "lex_bot"]);
export type ReferenceFieldType = z.infer<typeof ReferenceFieldTypeSchema>;

export const FieldTypeSchema = z.enum([...ContentFieldTypeSchema.options, ...ReferenceFieldTypeSchema.options]);
export type FieldType = z.infer<typeof FieldTypeSchema>;

export function isReferenceType(type: FieldType): type is ReferenceFieldType {
  return (ReferenceFieldTypeSchema.options as readonly string[]).includes(type);
}

/**
 * Snake case only. The name is read from a contact flow as
 * `$.External.announcements.hold_1`, and Connect's own guidance on
 * attribute values is unreliable enough that keeping keys to
 * alphanumerics and underscore removes the question.
 */
export const FieldNameSchema = z.string().regex(/^[a-z0-9][a-z0-9_]{0,63}$/);

/** `${type}/${name}` - the same address the audit log records a change against. */
export const FieldKeySchema = z.string().regex(/^[a-z_]+\/[a-z0-9][a-z0-9_]{0,63}$/);
export function fieldKey(type: FieldType, name: string): string {
  return `${type}/${name}`;
}
export function parseFieldKey(key: string): { type: FieldType; name: string } {
  const [type, name] = key.split("/", 2);
  return { type: FieldTypeSchema.parse(type), name: FieldNameSchema.parse(name) };
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
/** Local wall time, `YYYY-MM-DDTHH:MM`, no offset - the same convention as the weekly windows. Lexical order is time order. */
export const LocalDateTimeSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):[0-5]\d$/, "a local date and time as YYYY-MM-DDTHH:MM");

/**
 * A holiday is a closed window, not a day (#265, the owner's "close for
 * Christmas at noon 12/24 and re-open 6am 12/27"): from one local wall
 * time to another in the schedule's zone, evaluated in the customer's
 * account at call time like the weekly windows. Closed all day is
 * `00:00` to the next day's `00:00`.
 */
export const HolidaySchema = z
  .object({
    name: z.string().min(1).max(128),
    from: LocalDateTimeSchema,
    to: LocalDateTimeSchema,
    /**
     * The name of one of the profile's announcements (the owner's ask of 2026-09-18: one per holiday, the customer
     * in control). The lander resolves it to the payload at call time; a value that names no announcement plays as
     * it is, so a sentence typed here still works. Absent, the schedule's "say when closed" plays.
     */
    announcement: z.string().max(4000).optional(),
  })
  .refine((h) => h.to > h.from, { message: "a holiday ends after it starts", path: ["to"] });
export type Holiday = z.infer<typeof HolidaySchema>;

/**
 * The one-day shape authored before #265: a date, closed all day or open
 * only between `opens` and `closes`. Still accepted, never written: a
 * stored row is read as windows through `holidayWindows` and saved as
 * them on its first save. No migration - a young codebase.
 */
export const LegacyHolidaySchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  name: z.string().min(1).max(128),
  closed: z.boolean(),
  opens: z.string().regex(HHMM).optional(),
  closes: z.string().regex(HHMM).optional(),
  announcement: z.string().max(4000).optional(),
});
export type LegacyHoliday = z.infer<typeof LegacyHolidaySchema>;

/** The calendar day after `YYYY-MM-DD` - a property of the date, no zone involved. */
export function nextDay(date: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * The windows a holiday of either shape stands for. A legacy closed day is
 * one window, `00:00` to the next day's `00:00`. A legacy part day meant
 * "open only between opens and closes that day": it becomes the closed
 * part(s) of the day - before `opens` and after `closes` - and the hours
 * between fall to the weekly schedule, which is the one reading that
 * differs (the old shape opened those hours whatever the week said).
 * `closes` of `23:59` reads as the end of the day, so no one-minute window
 * is made of it.
 */
export function holidayWindows(h: Holiday | LegacyHoliday): Holiday[] {
  if (!("date" in h)) return [h];
  const said = h.announcement ? { announcement: h.announcement } : {};
  const allDay = { name: h.name, from: `${h.date}T00:00`, to: `${nextDay(h.date)}T00:00`, ...said };
  if (h.closed || !h.opens || !h.closes) return [allDay];
  const out: Holiday[] = [];
  if (h.closes > h.opens) {
    if (h.opens !== "00:00") out.push({ name: h.name, from: `${h.date}T00:00`, to: `${h.date}T${h.opens}`, ...said });
    if (h.closes !== "23:59") out.push({ name: h.name, from: `${h.date}T${h.closes}`, to: `${nextDay(h.date)}T00:00`, ...said });
  } else if (h.closes < h.opens) {
    // Open past midnight: the closed part of the day is between the close and the open.
    out.push({ name: h.name, from: `${h.date}T${h.closes}`, to: `${h.date}T${h.opens}`, ...said });
  }
  return out;
}

/**
 * Hours are authored as a schedule but never returned as one: the lander
 * evaluates them and returns `open: false` plus the announcement to
 * play. Business logic does not belong in a contact flow (section 10.3).
 */
export const HoursScheduleSchema = z.object({
  /** IANA zone. Evaluated in the customer's account at call time. */
  timezone: z.string().min(1).max(64),
  weekly: z
    .array(
      z.object({
        day: z.number().int().min(0).max(6),
        opens: z.string().regex(HHMM),
        closes: z.string().regex(HHMM),
      }),
    )
    .max(21),
  /** Closed windows (#265). The one-day shape authored before is accepted and read as windows - see `holidayWindows`. */
  holidays: z
    .array(z.union([HolidaySchema, LegacyHolidaySchema]))
    .max(80)
    .transform((list) => list.flatMap(holidayWindows))
    .optional(),
  /** The announcement played outside the windows: a name of one of the profile's announcements, resolved by the lander, or literal text. */
  closed_announcement: z.string().max(4000).optional(),
  /**
   * ADR-0019: the Connect hours of operation this schedule IS - its logical name in the environment's inventory, resolved
   * to the ARN centrally like any reference. With it, the customer's stack writes the windows, the time zone, the holidays
   * and an emergency close into that hours of operation, so Connect's own Check hours of operation block agrees with the
   * lander. Without it, the schedule is the lander's alone, as before.
   */
  connect_hours: z.string().min(1).max(127).optional(),
});
export type HoursSchedule = z.infer<typeof HoursScheduleSchema>;
/** A schedule before parsing: holidays of either shape. */
export type HoursScheduleInput = z.input<typeof HoursScheduleSchema>;

/** Connect's own shape for a day's window, as DescribeHoursOfOperation returns it and UpdateHoursOfOperation takes it. */
export const ConnectHoursDaySchema = z.enum(["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"]);
export const ConnectHoursEntrySchema = z.object({
  day: ConnectHoursDaySchema,
  /** HH:MM, 24-hour; 00:00 as an end means midnight. */
  start: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  end: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
});
export type ConnectHoursEntry = z.infer<typeof ConnectHoursEntrySchema>;

/** What the inventory carries for a hours of operation (ADR-0019): its current windows, zone and overrides, so the portal can show "in Connect now" and seed from it. */
export const HoursOfOperationDetailSchema = z.object({
  timezone: z.string().min(1).max(64),
  config: z.array(ConnectHoursEntrySchema).max(100),
  overrides: z
    .array(
      z.object({
        id: z.string().min(1).max(256),
        name: z.string().min(1).max(127),
        from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        till: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        config: z.array(ConnectHoursEntrySchema).max(100),
      }),
    )
    .max(200),
});
export type HoursOfOperationDetail = z.infer<typeof HoursOfOperationDetailSchema>;

/** A menu is an array of objects because `JSON` response validation allows it (section 10.3). */
export const MenuOptionSchema = z.object({
  dtmf: z.string().regex(/^[0-9*#]$/),
  label: z.string().min(1).max(128),
  target: z.object({ type: ReferenceFieldTypeSchema, name: FieldNameSchema }),
});
export type MenuOption = z.infer<typeof MenuOptionSchema>;
export const MenuSchema = z.array(MenuOptionSchema).max(12);

const named = { name: FieldNameSchema };

/**
 * What a key-value pair's value is (#265): text, a number, true/false, or
 * a Connect object named from the inventory - checked at publish like a
 * reference field, so drift, aliases and promotion validation apply, and
 * delivered to the flow as the ARN. Absent means text, so nothing
 * authored before changes.
 */
export const KvKindSchema = z.enum(["text", "number", "boolean", ...ReferenceFieldTypeSchema.options]);
export type KvKind = z.infer<typeof KvKindSchema>;

export function isConnectKind(kind: KvKind): kind is ReferenceFieldType {
  return (ReferenceFieldTypeSchema.options as readonly string[]).includes(kind);
}

/** Why `value` is not a `kind`, as a sentence, or undefined when it is. One rule for the editor, the CSV import and the schema. */
export function kvValueProblem(kind: KvKind, value: string): string | undefined {
  const v = value.trim();
  switch (kind) {
    case "text":
      return undefined;
    case "number":
      return v !== "" && Number.isFinite(Number(v)) ? undefined : `"${value}" is not a number.`;
    case "boolean":
      return /^(true|false)$/i.test(v) ? undefined : `"${value}" is not true or false.`;
    default:
      return v ? undefined : `A ${kind.replace(/_/g, " ")} is chosen by name from the Connect cache.`;
  }
}

/** What the flow receives for a pair of this kind - a Connect kind's ARN is resolved centrally, this is the rest. */
export function kvWireValue(kind: KvKind, value: string): string | number | boolean {
  if (kind === "number") return Number(value.trim());
  if (kind === "boolean") return value.trim().toLowerCase() === "true";
  return value;
}

export const KvFieldSchema = z
  .object({ ...named, type: z.literal("kv"), kind: KvKindSchema.optional(), value: z.string().max(1024) })
  .superRefine((f, ctx) => {
    const problem = kvValueProblem(f.kind ?? "text", f.value);
    if (problem) ctx.addIssue({ code: "custom", path: ["value"], message: problem });
  });
export type KvField = z.infer<typeof KvFieldSchema>;

/**
 * One authored field. Adding a type is data, not a deployment: a new
 * content type is a new key, a new reference type a new inventory
 * category (section 10.2).
 */
export const ProfileFieldSchema = z.discriminatedUnion("type", [
  z.object({ ...named, type: z.literal("announcement"), value: z.string().min(1).max(4000) }),
  KvFieldSchema,
  z.object({ ...named, type: z.literal("voice"), value: z.string().regex(/^[A-Za-z]{2,32}$/) }),
  z.object({ ...named, type: z.literal("hours"), value: HoursScheduleSchema }),
  z.object({ ...named, type: z.literal("menu"), value: MenuSchema }),
  ...ReferenceFieldTypeSchema.options.map((t) =>
    // A logical name ("Tier 1 Queue"), resolved through the inventory. Customers never see an ARN.
    z.object({ ...named, type: z.literal(t), ref: z.string().min(1).max(256) }),
  ),
]);
export type ProfileField = z.infer<typeof ProfileFieldSchema>;

export function fieldAddress(field: ProfileField): string {
  return fieldKey(field.type, field.name);
}

/** `Profiles` - PK `organisation_id`, SK `profile_id`. Environment-agnostic by design. */
export const ProfileRecordSchema = z.object({
  organisation_id: z.string().min(1),
  profile_id: z.string().min(1),
  /** What the customer calls it: "Global Helpdesk". */
  name: z.string().min(1).max(128),
  description: z.string().max(512).optional(),
  created_by: z.string().min(1),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
});
export type ProfileRecord = z.infer<typeof ProfileRecordSchema>;

/**
 * Per-field provenance, per environment (section 10.6). The
 * locally-modified marker - not the timestamp - decides whether a
 * promotion asks about a field: comparing timestamps alone loses a
 * production change whenever a later source change looks newer, which is
 * exactly when both sides were active.
 */
export const FieldMetaSchema = z.object({
  last_changed_at: z.string().datetime({ offset: true }),
  last_changed_by: z.string().min(1),
  /** Set when this environment edits the field directly; cleared when a promotion resolves it. */
  locally_modified: z.boolean(),
});
export type FieldMeta = z.infer<typeof FieldMetaSchema>;

/**
 * `ConfigurationVersions` - PK `${organisation_id}#${environment_id}#${profile_id}`,
 * SK `v` + zero-padded version. Immutable: publishing appends, and the
 * highest version is current, so rollback is a republish of an older
 * body rather than a mutation.
 */
export const ConfigurationVersionRecordSchema = z.object({
  pk: z.string().min(1),
  /** `v0000000012` - zero-padded so lexical order is numeric order. */
  sk: z.string().regex(/^v\d{10}$/),
  organisation_id: z.string().min(1),
  environment_id: z.string().min(1),
  profile_id: z.string().min(1),
  config_version: z.number().int().positive(),
  fields: z.array(ProfileFieldSchema),
  /** Keyed by `${type}/${name}`. */
  field_meta: z.record(FieldKeySchema, FieldMetaSchema),
  published_by: z.string().min(1),
  published_at: z.string().datetime({ offset: true }),
  /** Set where this version was produced by a promotion or a rollback rather than a direct edit. */
  source: z.enum(["direct", "promotion", "rollback"]),
  source_environment_id: z.string().optional(),
  rolled_back_from_version: z.number().int().positive().optional(),
});
export type ConfigurationVersionRecord = z.infer<typeof ConfigurationVersionRecordSchema>;

/**
 * Phone numbers and Lambda functions are inventoried but are not reference
 * types: a profile never points at one. The binding points at a number, and
 * the authoring UI needs the real list to offer; the Lambda functions are
 * what the instance's flows may invoke (#353, #347), listed with
 * ListLambdaFunctions because ListIntegrationAssociations cannot list them.
 */
export const InventoryTypeSchema = z.enum([...ReferenceFieldTypeSchema.options, "phone_number", "lambda_function"]);
export type InventoryType = z.infer<typeof InventoryTypeSchema>;

/**
 * `Inventories` - PK `environment_id`, SK `${type}#${name}`. What this
 * environment's Connect instance actually has, pushed by the customer
 * account on a schedule. Configuration metadata, not CX content
 * (ADR-0006), so it is central and powers the authoring dropdowns.
 */
export const InventoryItemRecordSchema = z.object({
  environment_id: z.string().min(1),
  /** `${type}#${logical name}` */
  sk: z.string().min(1),
  type: InventoryTypeSchema,
  /** The human-readable name in the customer's instance, as they see it there. */
  name: z.string().min(1).max(256),
  arn: z.string().min(1),
  /** Connect's own id where it differs from the ARN tail (queues, flows). */
  resource_id: z.string().optional(),
  /** ADR-0019: a hours of operation's current windows, zone and overrides; absent for every other type. */
  detail: HoursOfOperationDetailSchema.optional(),
  /** #352: a flow a number routes to, linted in the customer's account - an empty list is clean, absent is not checked. */
  findings: z.array(FlowFindingSchema).optional(),
  /** #352: a phone number's inbound flow, by ARN. */
  flow_arn: z.string().optional(),
  /** #347: a Lex bot alias's conversation log group ARN; absent for every other type. */
  log_group_arn: z.string().optional(),
  observed_at: z.string().datetime({ offset: true }),
});
export type InventoryItemRecord = z.infer<typeof InventoryItemRecordSchema>;

/** The `__state__` row on `Inventories` - ADR-0018 section 3, as central keeps it. SK is the literal `__state__`. */
export const InventoryStateRecordSchema = z.object({
  environment_id: z.string().min(1),
  sk: z.literal("__state__"),
  state: z.enum(["settled", "changing"]),
  settled_at: z.string().datetime({ offset: true }).optional(),
  last_change_at: z.string().datetime({ offset: true }).optional(),
  burst_calls: z.number().int().nonnegative().optional(),
  burst_principal: z.string().optional(),
  burst_first_call_at: z.string().datetime({ offset: true }).optional(),
  burst_last_call_at: z.string().datetime({ offset: true }).optional(),
  /** Central's own counter: moves on every push that replaced items, whatever template sent it. A promotion preview carries the version it was computed against. */
  version: z.number().int().nonnegative(),
  observed_at: z.string().datetime({ offset: true }),
});
export type InventoryStateRecord = z.infer<typeof InventoryStateRecordSchema>;

/**
 * `ReferenceAliases` - PK `environment_id`, SK `${type}#${logical name}`.
 *
 * Section 10.2: where the same name exists in every environment - the
 * normal case - a logical reference resolves automatically through each
 * environment's own inventory, and nothing needs configuring. This is the
 * manual override for where the names diverge: "Tier 1 Queue" in dev is
 * "Tier1Queue" in prod. Per environment, because the divergence is.
 */
export const ReferenceAliasRecordSchema = z.object({
  environment_id: z.string().min(1),
  /** `${type}#${logical name}` */
  sk: z.string().min(1),
  type: ReferenceFieldTypeSchema,
  /** The name the profile uses. */
  name: z.string().min(1).max(256),
  /** The name this environment's instance actually has. */
  resolves_to: z.string().min(1).max(256),
  set_by: z.string().min(1),
  set_at: z.string().datetime({ offset: true }),
});
export type ReferenceAliasRecord = z.infer<typeof ReferenceAliasRecordSchema>;

/** `Bindings` - PK `environment_id`, SK the E.164 number. One number reaches one profile. */
/**
 * ADR-0020: what a binding is keyed by. A phone number in E.164 is one
 * shape of alias - the one Connect hands the lander as the dialed
 * number; a name in lower case is the other - what a chat or email flow,
 * or one number that fans into twenty, passes as `alias`.
 */
export const BindingAliasSchema = z.string().regex(/^(\+[1-9]\d{6,14}|[a-z0-9][a-z0-9_.-]{0,63})$/, "an alias is a phone number in E.164 (+16615093462) or a short lower-case name (sales, webchat-support)");
export const aliasKind = (alias: string): "phone_number" | "name" => (alias.startsWith("+") ? "phone_number" : "name");

export const BindingRecordSchema = z.object({
  environment_id: z.string().min(1),
  /** The alias - a phone number or a name (ADR-0020). */
  alias: BindingAliasSchema,
  organisation_id: z.string().min(1),
  profile_id: z.string().min(1),
  /** Set where the customer bound this manually rather than by matching names across the chain. */
  manual: z.boolean(),
  bound_by: z.string().min(1),
  bound_at: z.string().datetime({ offset: true }),
});
export type BindingRecord = z.infer<typeof BindingRecordSchema>;

/**
 * `Overrides` - PK `${organisation_id}#${environment_id}`, SK
 * `${profile_id}#${type}/${name}`.
 *
 * A layer over the configuration, never an edit to it: never promoted,
 * never in a promotion diff, and it stops applying by itself. The window
 * is evaluated in the customer's account at call time, so an expiry
 * takes effect on schedule even if a sync is late (section 10.6).
 */
export const OverrideRecordSchema = z.object({
  pk: z.string().min(1),
  sk: z.string().min(1),
  organisation_id: z.string().min(1),
  environment_id: z.string().min(1),
  profile_id: z.string().min(1),
  field: FieldKeySchema,
  /** Same shape as the field it masks, minus the address. */
  value: z.unknown(),
  starts_at: z.string().datetime({ offset: true }),
  /** Absent means indefinite - allowed, and surfaced as aging rather than expired. */
  expires_at: z.string().datetime({ offset: true }).optional(),
  reason: z.string().min(1).max(512),
  set_by: z.string().min(1),
  set_at: z.string().datetime({ offset: true }),
});
export type OverrideRecord = z.infer<typeof OverrideRecordSchema>;

export const PromotionStatusSchema = z.enum(["pending_approval", "applied", "rejected"]);
export type PromotionStatus = z.infer<typeof PromotionStatusSchema>;

/** What the admin chose for one contested field. Default is keep, so overwriting is opt-in. */
export const PromotionDecisionSchema = z.object({
  field: FieldKeySchema,
  choice: z.enum(["take_source", "keep_target"]),
});
export type PromotionDecision = z.infer<typeof PromotionDecisionSchema>;

/** `Promotions` - PK `organisation_id`, SK `${created_at}#${promotion_id}`. */
export const PromotionRecordSchema = z.object({
  organisation_id: z.string().min(1),
  sk: z.string().min(1),
  promotion_id: z.string().min(1),
  profile_id: z.string().min(1),
  /**
   * A gated direct edit rides this same record: ADR-0008 gates "any
   * change to that environment's configuration, whether a promotion into
   * it or a direct edit of it", and the approval path is identical, so
   * modeling them apart would duplicate it. `proposed_fields` carries
   * what a direct edit or rollback wants to publish; a promotion reads
   * its values from the source version instead.
   */
  kind: z.enum(["promotion", "direct_edit", "rollback"]),
  proposed_fields: z.array(ProfileFieldSchema).optional(),
  rollback_of_version: z.number().int().positive().optional(),
  source_environment_id: z.string().min(1),
  target_environment_id: z.string().min(1),
  source_version: z.number().int().positive(),
  /** The target version the diff was computed against; a later publish invalidates it. */
  target_version: z.number().int().nonnegative(),
  /** ADR-0018 section 4: the target's inventory version the preview was computed against; applying refuses if it has moved. */
  target_inventory_version: z.number().int().nonnegative().optional(),
  status: PromotionStatusSchema,
  decisions: z.array(PromotionDecisionSchema),
  requested_by: z.string().min(1),
  requested_at: z.string().datetime({ offset: true }),
  /** #264: a gated non-manager's saves accumulate into the one open request - who last touched it, and when. */
  updated_by: z.string().optional(),
  updated_at: z.string().datetime({ offset: true }).optional(),
  decided_by: z.string().optional(),
  decided_at: z.string().datetime({ offset: true }).optional(),
  /** ADR-0008: an admin pushing a gated change through where the customer enabled it. */
  break_glass: z.boolean().optional(),
  applied_version: z.number().int().positive().optional(),
});
export type PromotionRecord = z.infer<typeof PromotionRecordSchema>;

/**
 * What syncs into the customer's account: this environment's current
 * version resolved to real ARNs, plus its active overrides. Schedules
 * travel resolved-but-unevaluated because the lander evaluates them at
 * call time; override windows travel for the same reason.
 */
export const SyncedOverrideSchema = z.object({
  field: FieldKeySchema,
  value: z.unknown(),
  starts_at: z.string().datetime({ offset: true }),
  expires_at: z.string().datetime({ offset: true }).optional(),
});

export const ResolvedConfigurationSchema = z.object({
  environment_id: z.string().min(1),
  profile_id: z.string().min(1),
  profile_name: z.string().min(1),
  config_version: z.number().int().positive(),
  resolved_at: z.string().datetime({ offset: true }),
  /** Every authored field, references already resolved to ARNs. */
  fields: z.array(z.object({ field: FieldKeySchema, value: z.unknown() })),
  overrides: z.array(SyncedOverrideSchema),
});
export type ResolvedConfiguration = z.infer<typeof ResolvedConfigurationSchema>;

/** One sync payload covers every profile bound in the environment. */
export const ConfigurationSyncPayloadSchema = z.object({
  environment_id: z.string().min(1),
  generated_at: z.string().datetime({ offset: true }),
  /** Alias -> profile id, this environment's bindings: a dialed number, or a name a flow passes (ADR-0020). */
  bindings: z.record(z.string(), z.string()),
  profiles: z.array(ResolvedConfigurationSchema),
});
export type ConfigurationSyncPayload = z.infer<typeof ConfigurationSyncPayloadSchema>;

/**
 * The lander's wire format (section 10.3). Bulk is the default and the
 * flow should call it once at entry: Connect charges an invocation per
 * `Invoke AWS Lambda function` block and caps a chain at 20 s, so a flow
 * that calls out per value is what made the previous implementation
 * cumbersome. Targeted mode is for the genuinely late lookup.
 */
export const LanderRequestSchema = z.object({
  /** ADR-0020: the alias the flow names - a contact attribute a chat, email or fan-out flow sets before invoking; empty means "use the dialed number". */
  alias: z.string().max(64).optional(),
  /** Connect sends the dialed number as the system endpoint. */
  dialled_number: z.string().min(1).optional(),
  profile: z.string().min(1).optional(),
  type: FieldTypeSchema.optional(),
  name: FieldNameSchema.optional(),
});
export type LanderRequest = z.infer<typeof LanderRequestSchema>;

/**
 * Yes/no as the lander says it (#351). Connect keeps a Lambda's strings in
 * `$.External` but renders a JSON boolean as "", so `open: false` could
 * never be compared with "false". Every scalar the lander returns is a
 * string for the same reason.
 */
export const ConnectBooleanSchema = z.enum(["true", "false"]);
export type ConnectBoolean = z.infer<typeof ConnectBooleanSchema>;

/** Decisions, not raw data: the flow is told open/closed, never the schedule. */
export const HoursDecisionSchema = z.object({
  open: ConnectBooleanSchema,
  reason: z.enum(["open", "closed", "holiday", "override"]),
  /** What to play, resolved: the named announcement's payload, else the text as authored. */
  announcement: z.string().optional(),
  /** The announcement's name when `announcement` came from one, so a flow can also fetch it by name. */
  announcement_name: z.string().optional(),
  /** Whether `announcement` is SSML (`<speak>…`), so a flow can pick the Play prompt block that interprets it. */
  announcement_ssml: ConnectBooleanSchema.optional(),
});
export type HoursDecision = z.infer<typeof HoursDecisionSchema>;

export const LanderResponseSchema = z.object({
  /** So a call can be traced to the exact configuration that drove it. */
  profile: z.string(),
  /** The version as digits (`"110"`): a flow reads strings only (#351). */
  config_version: z.string().regex(/^\d+$/),
  resolved_at: z.string(),
  announcements: z.record(z.string(), z.string()).optional(),
  /** Text as typed; a number as its digits and true/false as the word (#351); a Connect kind's ARN, with `<name>_name` beside it (#265). */
  kv: z.record(z.string(), z.string()).optional(),
  voices: z.record(z.string(), z.string()).optional(),
  queues: z.record(z.string(), z.string()).optional(),
  flows: z.record(z.string(), z.string()).optional(),
  prompts: z.record(z.string(), z.string()).optional(),
  hours_of_operation: z.record(z.string(), z.string()).optional(),
  lex_bots: z.record(z.string(), z.string()).optional(),
  menus: z.record(z.string(), MenuSchema).optional(),
  hours: z.record(z.string(), HoursDecisionSchema).optional(),
  /** Targeted mode returns the one value here rather than a group. */
  value: z.unknown().optional(),
  /** Set when the lander answered from a safe default because it had nothing to read. */
  degraded: z.literal("true").optional(),
});
export type LanderResponse = z.infer<typeof LanderResponseSchema>;

/** Wire group name per type: `announcement` -> `announcements`, so a flow reads `$.External.announcements.hold_1`. */
export const WIRE_GROUP: Record<FieldType, string> = {
  announcement: "announcements",
  kv: "kv",
  voice: "voices",
  hours: "hours",
  menu: "menus",
  queue: "queues",
  flow: "flows",
  prompt: "prompts",
  hours_of_operation: "hours_of_operation",
  lex_bot: "lex_bots",
};

/* -------------------------------------------------------------------------
 * Portal API contracts for the configuration module.
 * ---------------------------------------------------------------------- */

export interface ProfileSummary {
  profileId: string;
  name: string;
  description?: string | undefined;
  configVersion?: number | undefined;
  publishedAt?: string | undefined;
  publishedBy?: string | undefined;
}
export interface ProfilesResponse {
  profiles: ProfileSummary[];
  environmentId: string;
}

/** POST .../profiles: the id the new profile was given. */
export interface ProfileCreatedResponse {
  profileId: string;
}

/** GET .../profiles/{profileId}/preview: what the lander would answer for this profile, before publishing (10.5). */
export interface ProfilePreviewResponse {
  resolved: ResolvedConfiguration;
  /** The lander's groups as it lays them out, headed by the profile's name and version. */
  wire: { profile: string; config_version: number } & Record<string, unknown>;
  sizeBytes: number;
  /** 10.3: a profile that cannot fit is the signal that it is really two profiles. */
  sizeLimitBytes: number;
  withinLimit: boolean;
}

export interface OverrideView {
  profileId: string;
  field: string;
  value: unknown;
  startsAt: string;
  expiresAt?: string | undefined;
  reason: string;
  setBy: string;
  setAt: string;
}

/** GET .../overrides: every active override in the environment, across profiles. */
/**
 * Section 11.6: the audit log is the undo log. A revert reads an entry's
 * `before` values and saves them again - a new version through the same
 * gate as any change, never an erasure. `fields` narrows it to some of the
 * entry's fields; absent means all of them.
 */
/**
 * The audit actions whose `changes` are the profile's own configuration,
 * and so can be reverted by saving the before values (11.6). An override
 * set or cleared, a binding, a role grant: reversed where they were made -
 * replaying an override's value into the profile would be a different
 * change, not an undo (found on the walk of 2026-09-21).
 */
export const REVERTIBLE_AUDIT_ACTIONS = ["profile.published", "rollback.applied", "promotion.applied"] as const;

export interface RevertInput {
  entryId: string;
  fields?: string[];
}

/** What a revert would do, field by field, before it is asked for: the current value is shown where the field has moved since. */
export interface RevertPreviewResponse {
  entryId: string;
  profileId: string;
  fields: Array<{
    field: string;
    /** The value the entry recorded before its change - what a revert restores; absent means the field would be removed. */
    restore?: unknown;
    /** What the entry changed it to. */
    after?: unknown;
    /** What the field holds now. */
    current?: unknown;
    /** The field has changed since the entry: a revert would overwrite a later change, and says so. */
    moved: boolean;
  }>;
  /** What a save by this caller does here (#264). */
  saveAuthority: "direct" | "self_approved" | "needs_approval";
}

export interface OverridesResponse {
  overrides: OverrideView[];
}

/** POST .../overrides: applied at once, never gated (ADR-0008); the managers are told rather than asked. */
export interface SetOverrideResponse {
  ok: true;
  notified: "managers";
}

/** POST .../announcements/preview: the audio, base64, as Polly rendered it. Nothing is stored. */
export interface AnnouncementPreviewResponse {
  contentType: string;
  audio: string;
  characters: number;
}

/**
 * The four conditions section 10.6 keeps apart: two are facts, two are
 * faults. A publish the environment has not pulled yet is a fact
 * (`pendingSync`) until it has had a fair chance to; only then is it a
 * fault (`syncLag`).
 */
export interface ConditionsView {
  /** No number in this environment reaches the profile, so nothing is served or pulled - a fact, with one thing to do. */
  unbound?: true | undefined;
  versionGap?: { behind: number; sourceEnvironmentId: string } | undefined;
  pendingSync?: { publishedVersion: number; publishedAt: string } | undefined;
  syncLag?: { publishedVersion: number; syncedVersion?: number | undefined; publishedAt: string; syncedAt?: string | undefined } | undefined;
  inventoryDrift?: Array<{ field: string; type: string; ref: string }> | undefined;
  staleOverrides: Array<{ field: string; expiresAt?: string | undefined; ageDays: number; setBy: string }>;
}

export interface ProfileDetailResponse {
  profile: { profileId: string; name: string; description?: string | undefined };
  environmentId: string;
  configVersion: number;
  fields: ProfileField[];
  fieldMeta: Record<string, FieldMeta>;
  publishedAt?: string | undefined;
  publishedBy?: string | undefined;
  overrides: OverrideView[];
  conditions: ConditionsView;
  /** ADR-0008: a customer with one environment has no promotion at all. */
  chain: string[];
  requiresApproval: boolean;
  breakGlassEnabled: boolean;
  /**
   * #264: what a save by this caller does here - published at once (`direct`, the
   * environment is not gated; `self_approved`, it is gated and the caller is a
   * manager or Gairm staff), or accumulated into one open change request for a
   * manager to decide (`needs_approval`).
   */
  saveAuthority: "direct" | "self_approved" | "needs_approval";
  /** The open change request holding a non-manager's saves, where one exists - the editor shows and extends it. */
  pendingRequest?: { promotionId: string; requestedBy: string; requestedAt: string; updatedBy?: string | undefined; updatedAt?: string | undefined; fields: ProfileField[] };
}

/** ADR-0018 section 4: what the API says about an instance's inventory - settled or mid-change, and at which version. */
export interface InventoryStateView {
  state: "settled" | "changing";
  version: number;
  settledAt?: string | undefined;
  lastChangeAt?: string | undefined;
  burst?: { calls: number; principal?: string | undefined; firstCallAt?: string | undefined; lastCallAt?: string | undefined };
}

export interface InventoryResponse {
  items: Array<{ type: InventoryType; name: string; arn: string; resourceId?: string | undefined; observedAt: string; detail?: HoursOfOperationDetail | undefined; findings?: FlowFinding[] | undefined; flowArn?: string | undefined }>;
  observedAt?: string | undefined;
  /** Only present where a name diverges; the normal case needs none. */
  aliases: Array<{ type: ReferenceFieldType; name: string; resolvesTo: string; setBy: string; setAt: string }>;
  /** Absent until the environment's stack has pushed an inventory with ADR-0018's state. */
  state?: InventoryStateView | undefined;
}

export interface BindingsResponse {
  bindings: Array<{ alias: string; kind: "phone_number" | "name"; profileId: string; manual: boolean; boundBy: string; boundAt: string }>;
}

export interface VersionsResponse {
  versions: Array<{
    configVersion: number;
    publishedAt: string;
    publishedBy: string;
    source: "direct" | "promotion" | "rollback";
    sourceEnvironmentId?: string | undefined;
    rolledBackFromVersion?: number | undefined;
    fieldCount: number;
    /** What this version did to the one before it, by field address - so a rollback target reads as content, not a number. */
    changes: VersionChange[];
  }>;
}

export interface VersionChange {
  field: string;
  kind: "added" | "changed" | "removed";
}

/** A Polly voice Connect can play an announcement in (neural or standard - generative is charged in Connect and left out). */
export interface VoiceView {
  id: string;
  name: string;
  language: string;
  languageCode: string;
  gender: string;
  engines: Array<"neural" | "standard">;
}

export interface VoicesResponse {
  voices: VoiceView[];
}

export interface PromotionDiffResponse {
  uncontested: Array<{ field: string; before?: unknown; after: unknown }>;
  contested: Array<{
    field: string;
    sourceValue: unknown;
    targetValue: unknown;
    sourceChangedAt: string;
    targetChangedAt: string;
    recommendation: "keep_target" | "take_source";
  }>;
  masked: Array<{
    field: string;
    incomingValue: unknown;
    override: { value: unknown; setBy: string; setAt: string; expiresAt?: string | undefined; reason: string };
  }>;
  unchanged: number;
  sourceEnvironmentId: string;
  targetEnvironmentId: string;
  sourceVersion: number;
  targetVersion: number;
  /** Permitted, and flagged: it leaves the skipped environment behind. */
  skipsEnvironments: string[];
  requiresApproval: boolean;
  /** ADR-0018 section 4: the target's inventory as previewed; the apply carries its version and is refused if it moved. */
  targetInventory?: InventoryStateView | undefined;
}

export interface PromotionsResponse {
  promotions: Array<{
    promotionId: string;
    profileId: string;
    kind: "promotion" | "direct_edit" | "rollback";
    status: PromotionStatus;
    sourceEnvironmentId: string;
    requestedBy: string;
    requestedAt: string;
    decidedBy?: string | undefined;
    decidedAt?: string | undefined;
    appliedVersion?: number | undefined;
  }>;
}

export type PublishResult =
  | { status: "published"; configVersion: number }
  | { status: "pending_approval"; promotionId: string };

export type PromotionResult =
  | { status: "applied"; configVersion: number; applied: string[]; kept: string[] }
  | { status: "pending_approval"; promotionId: string; contested: number };

/** POST .../promotions/{promotionId}/decision: approved and applied as a new version, or rejected. */
export type PromotionDecisionResponse = { status: "applied"; configVersion: number } | { status: "rejected" };
